// Owned by task 2.1 of the dashboard-redesign spec.
//
// Reads the pence-native arrears summary from the `reports` spec's
// `GET /reports/aged-debtors-summary` endpoint (a dashboard-shaped variant of
// aged-debtors that returns integer pence, the four-band `buckets` object, and
// a `perBuilding` breakdown, and supports `buildingId=ALL` aggregation). The
// reports spec owns this endpoint; it reconciles to the GL Debtors Control
// account per Constitution Rule 2. This hook is READ-ONLY: no mutation, no
// re-aggregation that bypasses the GL.
//
// (The sibling `GET /reports/aged-debtors` endpoint returns the full
// AgedDebtorsReport in pounds with array buckets and powers the standalone
// Aged Debtors report page — a different contract, intentionally not reused
// here.)
//
// Fetch contract (see design.md §Low-Level Design §Fetch contract):
//   - Starts in `{ status: "loading" }`.
//   - On mount and every `scope` change: an `AbortController`-guarded GET.
//   - Empty state when `totalPence === 0` AND all four buckets are zero.
//   - Error state carries an `errorId` (from the `X-Error-Id` response header
//     if present, else a locally-generated UUID v4) so the scoped error card
//     can render it verbatim for support (Requirement 14).
//   - `retry` re-invokes the fetch by bumping a retry counter.
//   - On unmount: aborts the in-flight fetch.
//
// The `api` client (frontend/src/services/api.ts) already attaches the Clerk
// JWT and the `x-clerk-subdomain` tenant header via interceptors — this hook
// MUST NOT re-implement any of that.

import { useCallback, useEffect, useState } from "react";
import type { AxiosError } from "axios";
import api from "../../../services/api";
import type { BuildingScope, WidgetResult, ArrearsAging } from "../types";

/** User-safe message shown in the scoped error card. Never leaks server text. */
const USER_SAFE_ERROR_MESSAGE = "Could not load arrears";

/**
 * Generate a UUID v4. Prefers `globalThis.crypto.randomUUID` when available;
 * falls back to a small `Math.random`-based hex formatter for environments
 * that don't expose the WebCrypto API (older test runners, JSDOM shims, etc.).
 * Never used to authenticate anything — only to correlate a UI error card to
 * a log entry when the server didn't return an `X-Error-Id` header.
 */
function generateLocalErrorId(): string {
  const cryptoRef = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  // RFC 4122 v4 shape: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx where y ∈ {8,9,a,b}.
  const rand = (): number => Math.floor(Math.random() * 16);
  const hex = (n: number): string => n.toString(16);
  let out = "";
  for (let i = 0; i < 36; i += 1) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      out += "-";
    } else if (i === 14) {
      out += "4";
    } else if (i === 19) {
      out += hex((rand() & 0x3) | 0x8);
    } else {
      out += hex(rand());
    }
  }
  return out;
}

/**
 * Extract an `X-Error-Id` header from an axios error's response. Case-
 * insensitive per RFC 7230; different transports normalise headers
 * differently (Node http lowercases everything, JSDOM leaves them as-is).
 */
function extractErrorIdFromAxios(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  const response = (err as AxiosError).response;
  if (!response || !response.headers) return undefined;
  const headers = response.headers as
    | Record<string, string | undefined>
    | { get?: (key: string) => string | null };
  // Some axios versions expose headers as an AxiosHeaders instance with a
  // `get(key)` method; others expose a plain record. Handle both defensively.
  if (typeof (headers as { get?: unknown }).get === "function") {
    const raw = (headers as { get: (key: string) => string | null }).get(
      "x-error-id",
    );
    if (typeof raw === "string" && raw.length > 0) return raw;
  }
  const record = headers as Record<string, string | undefined>;
  const candidate =
    record["x-error-id"] ??
    record["X-Error-Id"] ??
    record["X-ERROR-ID"];
  return typeof candidate === "string" && candidate.length > 0
    ? candidate
    : undefined;
}

/**
 * Validate that an unknown response body has the exact shape of `ArrearsAging`.
 * Returns the typed value on success or `undefined` on any structural
 * mismatch — the caller then transitions to the error state per the fetch
 * contract, so we never trust an unexpected payload.
 */
function parseArrearsAging(body: unknown): ArrearsAging | undefined {
  if (!body || typeof body !== "object") return undefined;
  const b = body as Record<string, unknown>;
  if (typeof b.totalPence !== "number" || !Number.isFinite(b.totalPence)) {
    return undefined;
  }
  const buckets = b.buckets;
  if (!buckets || typeof buckets !== "object") return undefined;
  const bk = buckets as Record<string, unknown>;
  const days0to30 = bk.days0to30;
  const days31to60 = bk.days31to60;
  const days61to90 = bk.days61to90;
  const days90plus = bk.days90plus;
  if (
    typeof days0to30 !== "number" ||
    typeof days31to60 !== "number" ||
    typeof days61to90 !== "number" ||
    typeof days90plus !== "number" ||
    !Number.isFinite(days0to30) ||
    !Number.isFinite(days31to60) ||
    !Number.isFinite(days61to90) ||
    !Number.isFinite(days90plus)
  ) {
    return undefined;
  }
  const perBuildingRaw = b.perBuilding;
  if (!Array.isArray(perBuildingRaw)) return undefined;
  const perBuilding: ArrearsAging["perBuilding"] = [];
  for (const row of perBuildingRaw) {
    if (!row || typeof row !== "object") return undefined;
    const r = row as Record<string, unknown>;
    if (
      typeof r.buildingId !== "string" ||
      typeof r.buildingName !== "string" ||
      typeof r.totalPence !== "number" ||
      !Number.isFinite(r.totalPence)
    ) {
      return undefined;
    }
    perBuilding.push({
      buildingId: r.buildingId,
      buildingName: r.buildingName,
      totalPence: r.totalPence,
    });
  }
  return {
    totalPence: b.totalPence,
    buckets: { days0to30, days31to60, days61to90, days90plus },
    perBuilding,
  };
}

function isEmpty(data: ArrearsAging): boolean {
  return (
    data.totalPence === 0 &&
    data.buckets.days0to30 === 0 &&
    data.buckets.days31to60 === 0 &&
    data.buckets.days61to90 === 0 &&
    data.buckets.days90plus === 0
  );
}

function buildingIdParam(scope: BuildingScope): string {
  return scope.kind === "all" ? "ALL" : scope.buildingId;
}

export function useDashboardArrears(
  scope: BuildingScope,
): WidgetResult<ArrearsAging> {
  const [retryCounter, setRetryCounter] = useState(0);
  const [result, setResult] = useState<WidgetResult<ArrearsAging>>({
    status: "loading",
  });

  const retry = useCallback(() => {
    setRetryCounter((n) => n + 1);
  }, []);

  const scopeKind = scope.kind;
  const scopeBuildingId = scope.kind === "single" ? scope.buildingId : null;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    // Reset to loading on scope/retry change so consumers see the skeleton
    // between fetches instead of stale ready data.
    setResult({ status: "loading" });

    const buildingId =
      scopeKind === "all" ? "ALL" : (scopeBuildingId ?? "ALL");
    const url = `/reports/aged-debtors-summary?buildingId=${encodeURIComponent(buildingId)}`;

    api
      .get(url, { signal: controller.signal })
      .then((response) => {
        if (cancelled) return;
        const parsed = parseArrearsAging(response?.data);
        if (!parsed) {
          setResult({
            status: "error",
            errorId: generateLocalErrorId(),
            message: "Unexpected response shape",
            retry,
          });
          return;
        }
        if (isEmpty(parsed)) {
          setResult({ status: "empty" });
        } else {
          setResult({ status: "ready", data: parsed });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // The browser/axios raises `CanceledError` (or a `DOMException`
        // named `AbortError`) when the AbortController fires. That path is
        // the caller's intent, not a failure — swallow silently.
        if (controller.signal.aborted) return;
        const errorId =
          extractErrorIdFromAxios(err) ?? generateLocalErrorId();
        setResult({
          status: "error",
          errorId,
          message: USER_SAFE_ERROR_MESSAGE,
          retry,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `scope.kind` and `scope.buildingId` are the only inputs; `retryCounter`
    // triggers a re-fetch on `retry()`. `retry` and `api` are stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKind, scopeBuildingId, retryCounter]);

  return result;
}
